// src/pages/POYarnFabric.js
import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Button, Stack,
  Box, Fab, Snackbar, Backdrop, Dialog, DialogTitle, DialogContent, Chip
} from '@mui/material';
import GestureIcon from '@mui/icons-material/Gesture';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';
import { fetchPendingYarnFabricPOs } from '../services/yarnFabricService';
import api from '../services/api';

const POYarnFabric = () => {
  const [allData, setAllData] = useState([]); // full list
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    const data = await fetchPendingYarnFabricPOs();
    setAllData(data); // 🌟 Store all records
    const uniqueKeys = new Set();
    const unique = data.filter(item => {
      const key = `${item.Po_ID}-${item.Po_No}-${item.CompanyID}`;
      return uniqueKeys.has(key) ? false : uniqueKeys.add(key);
    });
    setRows(unique);

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleApprove = async () => {
    setBackdropOpen(true);
    try {
      const response = await api.post('/api/approve-po', {
        poid: selectedRow.Po_ID,
        finyear: selectedRow.Po_Finyear,
        ccode: selectedRow.CompanyID
      });
      setSnackbar({ open: true, message: response.data.message });
      await fetchData();
    } catch (err) {
      console.error("Approval error:", err);
      setSnackbar({ open: true, message: 'Approval failed' });
    } finally {
      setBackdropOpen(false);
      setDialogOpen(false);
    }
  };

  const handleDetails = (row) => {
    const filtered = allData.filter(r => r.Po_ID === row.Po_ID);
    setDialogData(filtered);
    setSelectedRow(row);
    setDialogOpen(true);
  };

  const getRowColor = (type) => type === 'YarnPO' ? '#fffde7' : '#e3f2fd';
  const getIcon = (type) => type === 'YarnPO' ? <GestureIcon fontSize="small" /> : <ReceiptIcon fontSize="small" />;
  const getCompanyName = (id) => id === 1 ? 'A' : 'T';
  const getCompanyColor = (id) => id === 1 ? 'success' : 'warning';

  return (
    <Container maxWidth="sm" sx={{ py: 2, position: 'relative' }}>
      <Typography variant="h6" gutterBottom>Pending Yarn / Fabric POs</Typography>
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '15%' }}>PO</TableCell>
                <TableCell sx={{ width: '15%' }}>Date</TableCell>
                <TableCell sx={{ width: '55%' }}>Supplier</TableCell>
                <TableCell sx={{ width: '15%' }}>Qty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={`${row.Po_ID}-${row.Clr_ID}`}
                  hover
                  sx={{ backgroundColor: getRowColor(row.Type), cursor: 'pointer' }}
                  onClick={() => handleDetails(row)}
                >
                  <TableCell>
                    <Stack spacing={0.5} alignItems="center">
                      <Chip label={getCompanyName(row.CompanyID)} size="small" color={getCompanyColor(row.CompanyID)} />
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {getIcon(row.Type)}
                        <Typography>{row.Po_No}</Typography>
                      </Stack>
                    </Stack>
                  </TableCell>
                  <TableCell>{new Date(row.Po_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Typography noWrap variant="body2" title={row.Name} sx={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.Name}</Typography>
                    <Typography variant="caption" color="text.secondary" fontSize={10}>
                      {row.BuyerOrderNo.includes(',') ? 'MANY' : row.BuyerOrderNo}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.Po_Qty}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Fab color="primary" sx={{ position: 'fixed', bottom: 16, right: 16 }} onClick={() => navigate(-1)}>
        <ArrowBackIcon />
      </Fab>
      {/* <Fab color="secondary" sx={{ position: 'fixed', bottom: 16, right: 16 }} onClick={() => navigate('/')}> <HomeIcon /> </Fab> */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      <Backdrop open={backdropOpen} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }} />

      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setConfirming(false); // 👈 reset confirm state on close
        }}
        PaperProps={{
          sx: {
            mx: '4px', // 4px side margins
            overflowY: 'auto',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ backgroundColor: getRowColor(dialogData[0]?.Type) }}>
          {dialogData[0]?.Type} - PO No {dialogData[0]?.Po_No} / {getCompanyName(dialogData[0]?.CompanyID)}
        </DialogTitle>
        <DialogContent>
          <Table size="small" sx={{ '& td, & th': { py: 0.25, px: 0.75 }, fontSize: '11px' }}>
            <TableHead>
              <TableRow>
                <TableCell>Buyer Order</TableCell>
                {dialogData[0]?.Type === 'YarnPO' ? (
                  <>
                    <TableCell>Yarn Count</TableCell>
                    <TableCell>Yarn Color</TableCell>
                    <TableCell>Kgs</TableCell>
                    <TableCell>Rate</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>Color</TableCell>
                    <TableCell>Fabric Desc</TableCell>
                    <TableCell>FDIA</TableCell>
                    <TableCell>Weight</TableCell>
                    <TableCell>Rate</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {dialogData.map((entry, i) => (
                <TableRow key={i}>
                  <TableCell>{entry.BuyerOrderNo}</TableCell>
                  {entry.Type === 'YarnPO' ? (
                    <>
                      <TableCell>{entry.count_descr}</TableCell>
                      <TableCell>{entry.clr_des}</TableCell>
                      <TableCell>{entry.Po_Qty}</TableCell>
                      <TableCell>{entry.Po_Rate}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{entry.FabricColour}</TableCell>
                      <TableCell>{entry.FabricType}</TableCell>
                      <TableCell>{entry.fdiadesc}</TableCell>
                      <TableCell>{entry.FabricCount}</TableCell>
                      <TableCell>{entry.Po_Rate}</TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box mt={2} display="flex" justifyContent="flex-end">
            {confirming ? (
              <Button
                variant="contained"
                color="warning"
                onClick={handleApprove}
                sx={{ transition: 'all 0.3s ease-in-out' }}
              >
                Are you sure?
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setConfirming(true)}
                sx={{ transition: 'all 0.3s ease-in-out' }}
              >
                Approve
              </Button>
            )}
          </Box>

        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default POYarnFabric;